import {
  isPercentScaledText,
  keepUnlessEditorTruncation,
  percentUnitFormatter
} from "@appretio/ui-kit";
import {
  CellValueChangedEvent,
  ColDef,
  GridApi,
  ValueFormatterParams,
  ValueGetterParams,
  ValueParserParams,
  ValueSetterParams
} from "ag-grid-enterprise";
import { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import type { ForecastTableControlValues } from "../../tableControls/forecastTableControlValues";
import {
  createMetricValueParser,
  formatCellValueByMetric,
  parseNumericOrRevert
} from "./columnUtils";
import {
  calculateOptionDerived,
  calculatePerSqm,
  isOptionDerivedMetric,
  isPerSqmMetric
} from "./derivedUtils";
import { PER_SQM_SUFFIX, STORE_SQM_METRIC } from "./metricConstants";
import { createRowId } from "./rowId";

/** Index of the reference value: 100 index points is a variance of 0 %. */
export const INDEX_BASE = 100;

/** Fraction decimals a `+/- Var` label shows, and the editor's entry cap. */
export const VARIANCE_DECIMALS = 2;

/**
 * Whether a plan-by mode reads a cell against the planning-year row. `IDX`
 * shows the index, `+/- Var` the same number minus 100 (index 126 -> 26 %), so
 * both run every path in this module.
 */
export function isIndexFamilyMode(
  planBy: ForecastTableControlValues["planBy"]
): boolean {
  return planBy === "IDX" || planBy === "+/- Var";
}

/**
 * Index points the display drops, added back on every entry. 0 for `IDX`,
 * which keeps its paths below arithmetically unchanged.
 */
function displayOffset(planBy: ForecastTableControlValues["planBy"]): number {
  return planBy === "+/- Var" ? INDEX_BASE : 0;
}

/**
 * Checks if a cell should display as an index value.
 * True when the row is NOT the planning year, the column is in indexPositiveColumns,
 * and the metric is in indexPositiveMetrics.
 */
export function isIndexCondition(
  fiscalYearName: string,
  metricName: string,
  colId: string,
  planningYear: string,
  metaData: ForecastMetaDataType
): boolean {
  return (
    fiscalYearName !== planningYear &&
    metaData.indexPositiveColumns.includes(colId) &&
    metaData.indexPositiveMetrics.includes(metricName)
  );
}

/**
 * Returns true if the year is a future year relative to ``planningYear`` —
 * i.e. its offset is >= the planning year's own offset. The planning year
 * itself is considered "future" (offset equal to its own).
 *
 * ``planningYear`` defaults to the current fiscal year (offset 0) when
 * omitted, matching the caller's own default. Every internal caller passes
 * the same ``planningYear`` it was given: the range-plan modal can anchor on
 * a year other than the current one (DAS-6693), and a row between "now" and
 * that anchor is otherwise misclassified as future by calendar offset alone.
 */
export function isFutureYear(
  yearName: string,
  metaData: ForecastMetaDataType,
  planningYear?: string
): boolean {
  const referenceOffset =
    planningYear === undefined ? 0 : (metaData.yearMapping[planningYear] ?? 0);
  return (metaData.yearMapping[yearName] ?? -1) >= referenceOffset;
}

/**
 * Whether a planning-year sibling row exists to index this series against
 * (e.g. an actual-only producer has no planning-year row at all). Shared by
 * `calculateIndex`, which falls back to the row's own raw value when this is
 * false, and `createIndexValueGetter`, which needs to know that a raw value
 * came back rather than a real index before applying the `+/- Var` offset.
 */
function hasPlanningYearReference(
  metricName: string,
  producerType: string,
  api: GridApi,
  planningYear: string
): boolean {
  const referenceRowId = createRowId(planningYear, metricName, producerType);
  return Boolean(api.getRowNode(referenceRowId)?.data);
}

/**
 * Calculates the index value for a cell relative to the planning year.
 * Past years: planningYearValue / rowValue * 100
 * Future years: rowValue / planningYearValue * 100
 */
export function calculateIndex(
  data: Record<string, unknown>,
  colId: string,
  api: GridApi,
  planningYear: string,
  metaData: ForecastMetaDataType
): number | null {
  const fiscalYearName = data.fiscal_year_name as string;
  const metricName = data.metric_name as string;
  const producerType = data.producer_type as string;

  const referenceRowId = createRowId(planningYear, metricName, producerType);
  const referenceNode = api.getRowNode(referenceRowId);

  if (!referenceNode?.data) {
    return data[colId] as number;
  }

  const rowValue = data[colId] as number;
  const planValue = referenceNode.data[colId] as number;

  if (!isFutureYear(fiscalYearName, metaData, planningYear)) {
    // Past year: planningYearValue / rowValue * 100
    if (rowValue === 0) return null;
    return (planValue / rowValue) * 100;
  } else {
    // Future year: rowValue / planningYearValue * 100
    if (planValue === 0) return null;
    return (rowValue / planValue) * 100;
  }
}

/**
 * Calculates the index for per-SQM metrics.
 * Uses the ratio: (sourceIndex / sqmIndex) * 100
 */
export function calculateIndexPerSqm(
  data: Record<string, unknown>,
  colId: string,
  api: GridApi,
  planningYear: string,
  metaData: ForecastMetaDataType
): number | null {
  const fiscalYearName = data.fiscal_year_name as string;
  const metricName = data.metric_name as string;
  const producerType = data.producer_type as string;

  // Get index for square meters (denominator)
  const sqmRowId = createRowId(fiscalYearName, STORE_SQM_METRIC, producerType);
  const sqmRow = api.getRowNode(sqmRowId);
  if (!sqmRow?.data) return null;
  const sqmIndex = calculateIndex(
    sqmRow.data,
    colId,
    api,
    planningYear,
    metaData
  );

  // Get index for source KPI (numerator)
  const sourceMetric = metricName.split(PER_SQM_SUFFIX)[0];
  const sourceRowId = createRowId(fiscalYearName, sourceMetric, producerType);
  const sourceRow = api.getRowNode(sourceRowId);
  if (!sourceRow?.data) return null;
  const sourceIndex = calculateIndex(
    sourceRow.data,
    colId,
    api,
    planningYear,
    metaData
  );

  if (sqmIndex && sqmIndex > 0 && sourceIndex && sourceIndex > 0) {
    return (sourceIndex / sqmIndex) * 100;
  }
  return null;
}

/**
 * Factory returning a ValueGetterFunc for an index-family mode.
 * Index rows display calculated index values; non-index rows display raw values.
 */
export function createIndexValueGetter(
  planBy: ForecastTableControlValues["planBy"],
  planningYear: string,
  metaData: ForecastMetaDataType
): (params: ValueGetterParams) => unknown {
  const offset = displayOffset(planBy);
  return (params: ValueGetterParams) => {
    const colId = params.column.getColId();
    const { fiscal_year_name, metric_name } = params.data;

    if (
      isIndexCondition(
        fiscal_year_name,
        metric_name,
        colId,
        planningYear,
        metaData
      )
    ) {
      const isPerSqm = metric_name.includes(PER_SQM_SUFFIX);
      const index = isPerSqm
        ? calculateIndexPerSqm(
            params.data,
            colId,
            params.api,
            planningYear,
            metaData
          )
        : calculateIndex(
            params.data,
            colId,
            params.api,
            planningYear,
            metaData
          );

      // A plain index cell with no planning-year sibling to index against
      // (e.g. an actual-only producer) falls back to its own raw absolute
      // inside `calculateIndex` — subtracting `offset` from that would
      // relabel an absolute as a fabricated variance. Per-SQM cells compute
      // their own null-on-missing-row semantics inside
      // `calculateIndexPerSqm`, so they don't need this guard.
      if (
        !isPerSqm &&
        !hasPlanningYearReference(
          metric_name,
          params.data.producer_type,
          params.api,
          planningYear
        )
      ) {
        return null;
      }

      // `calculateIndex` can hand back `undefined` (no reference row and no key
      // for the column), and `undefined - offset` is NaN — which the
      // formatter's null/undefined guard misses, painting the string "NaN".
      return Number.isFinite(index) ? (index as number) - offset : null;
    }

    // Non-index rows: compute derived metrics, otherwise return raw value
    if (isPerSqmMetric(metric_name)) {
      return calculatePerSqm(params.data, colId, params.api);
    }
    if (isOptionDerivedMetric(metric_name)) {
      const { fiscal_year_name, producer_type } = params.data;
      return calculateOptionDerived(
        metric_name,
        fiscal_year_name,
        producer_type,
        colId,
        params.api
      );
    }
    return params.data[colId];
  };
}

/**
 * Factory returning a ValueSetterFunc for an index-family mode.
 *
 * Non-index rows: standard absolute set.
 * Past-year index rows: apply change ratio to planning year row via setDataValue.
 * Future-year index rows: convert index to absolute value.
 */
export function createIndexValueSetter(
  planBy: ForecastTableControlValues["planBy"],
  planningYear: string,
  metaData: ForecastMetaDataType
): (params: ValueSetterParams) => boolean {
  const offset = displayOffset(planBy);
  return (params: ValueSetterParams): boolean => {
    const colId = params.column.getColId();
    const { fiscal_year_name, metric_name, producer_type } = params.data;

    if (
      !isIndexCondition(
        fiscal_year_name,
        metric_name,
        colId,
        planningYear,
        metaData
      )
    ) {
      // Non-index row: standard absolute behavior
      params.data[colId] = params.newValue === null ? 0 : params.newValue;
      return true;
    }

    // A cleared cell is index 0 — absolute 0, as it is in ABS mode.
    const newIndex =
      params.newValue == null ? 0 : Number(params.newValue) + offset;
    if (!Number.isFinite(newIndex)) return false;

    const planRowId = createRowId(planningYear, metric_name, producer_type);
    const planRowNode = params.api.getRowNode(planRowId);
    if (!planRowNode?.data) return false;

    if (!isFutureYear(fiscal_year_name, metaData, planningYear)) {
      // Past year: apply change ratio to planning year.
      // A blank display carries no index to invert — reject, as IDX does. The
      // offset would otherwise turn it into a phantom index of 100 and scale
      // the plan row off a cell that has no computable variance.
      if (params.oldValue == null) return false;
      const oldIndex = Number(params.oldValue) + offset;
      if (!oldIndex || !Number.isFinite(oldIndex)) return false;
      const changeRatio = newIndex / oldIndex;
      const planOldValue = planRowNode.data[colId] as number;
      const planNewValue = planOldValue * changeRatio;
      // setDataValue auto-fires onCellValueChanged for the planning year row
      planRowNode.setDataValue(colId, planNewValue);
      return false; // don't update the source (index display) cell
    } else {
      // Future year: convert index to absolute
      const planValue = planRowNode.data[colId] as number;
      const absoluteValue = (planValue * newIndex) / INDEX_BASE;
      params.data[colId] = absoluteValue;
      return true;
    }
  };
}

/**
 * Absolute ``value`` / ``oldValue`` for a cell change made in an index-family
 * mode.
 *
 * AG Grid reports both through the value getter, so on an index cell they
 * are index points (variance points in ``+/- Var``) — but every consumer
 * treats them as the metric's absolute. Without this translation a typed
 * index of 150 is stored as 150 pieces, and the cell re-renders as index 0.
 *
 * Past-year index edits don't need it: ``createIndexValueSetter`` routes
 * them through the planning-year row, which is never an index cell.
 */
export function toAbsoluteCellChange(
  event: CellValueChangedEvent,
  planBy: ForecastTableControlValues["planBy"],
  planningYear: string,
  metaData: ForecastMetaDataType
): Pick<CellValueChangedEvent, "value" | "oldValue"> {
  const asReported = { value: event.value, oldValue: event.oldValue };
  const colId = event.column.getColId();
  const { fiscal_year_name, metric_name, producer_type } = event.data;

  if (
    !isIndexFamilyMode(planBy) ||
    !isIndexCondition(
      fiscal_year_name,
      metric_name,
      colId,
      planningYear,
      metaData
    ) ||
    !isFutureYear(fiscal_year_name, metaData, planningYear)
  ) {
    return asReported;
  }

  const planRowId = createRowId(planningYear, metric_name, producer_type);
  const planValue = event.api.getRowNode(planRowId)?.data?.[colId];
  if (typeof planValue !== "number") return asReported;

  // A blank old display carries no index to invert — absolute 0, as in IDX.
  const oldIndex =
    event.oldValue == null ? 0 : Number(event.oldValue) + displayOffset(planBy);

  return {
    // The setter already wrote the absolute onto the row.
    value: event.data[colId],
    oldValue: Number.isFinite(oldIndex)
      ? (planValue * oldIndex) / INDEX_BASE
      : 0
  };
}

/**
 * Factory returning a ValueParserFunc for INDEX mode.
 *
 * Index cells are index points, so a "%" in the pasted text is not a unit
 * marker there — reading it as a fraction would send a ~100x-off change ratio
 * to the planning-year row. Non-index cells (including the planning-year row of
 * a percentage metric, which renders "60.54 %") keep the percent round-trip.
 */
export function createIndexValueParser(
  planBy: ForecastTableControlValues["planBy"],
  planningYear: string,
  metaData: ForecastMetaDataType
): (params: ValueParserParams) => unknown {
  const parseMetricValue = createMetricValueParser(metaData);
  return (params: ValueParserParams) => {
    const { fiscal_year_name, metric_name } = params.data;

    if (
      isIndexCondition(
        fiscal_year_name,
        metric_name,
        params.column.getColId(),
        planningYear,
        metaData
      )
    ) {
      return planBy === "+/- Var"
        ? parseVarianceOrRevert(params)
        : parseNumericOrRevert(params);
    }

    return parseMetricValue(params);
  };
}

/** A variance label carries "%" as its unit: strip it, then parse as a number. */
function parseVarianceOrRevert(params: ValueParserParams) {
  const raw =
    typeof params.newValue === "string"
      ? params.newValue.trim()
      : params.newValue;
  const isPercentScaled = isPercentScaledText(raw);
  const parsed = parseNumericOrRevert(
    isPercentScaled
      ? { ...params, newValue: String(raw).slice(0, -1).trim() }
      : params
  );
  // The cell holds an unrounded ratio while the editor's `precision` truncates
  // the value it seeds, so confirming an untouched cell would write the
  // truncation as a real edit.
  return keepUnlessEditorTruncation(params, parsed, VARIANCE_DECIMALS);
}

/**
 * Cell editor for an index-family mode. `+/- Var` caps entry at the 2 decimals
 * the label shows; every other cell keeps the grid's default editor.
 */
export function createIndexCellEditorSelector(
  planBy: ForecastTableControlValues["planBy"],
  planningYear: string,
  metaData: ForecastMetaDataType
): ColDef["cellEditorSelector"] {
  return (params) => {
    if (planBy !== "+/- Var" || !params.data) return undefined;
    const { fiscal_year_name, metric_name } = params.data;
    return isIndexCondition(
      fiscal_year_name,
      metric_name,
      params.column.getColId(),
      planningYear,
      metaData
    )
      ? {
          component: "agNumberCellEditor",
          params: { precision: VARIANCE_DECIMALS }
        }
      : undefined;
  };
}

/**
 * Factory returning a ValueFormatterFunc for an index-family mode.
 * Index rows display as integers — as `"26.00 %"` in `+/- Var`; non-index rows
 * use standard metric formatting.
 */
export function createIndexValueFormatter(
  planBy: ForecastTableControlValues["planBy"],
  planningYear: string,
  metaData: ForecastMetaDataType
): (params: ValueFormatterParams) => string {
  return (params: ValueFormatterParams): string => {
    if (params.value === null || params.value === undefined) {
      return "";
    }

    const { fiscal_year_name, metric_name } = params.data;
    const colId = params.column.getColId();

    if (
      isIndexCondition(
        fiscal_year_name,
        metric_name,
        colId,
        planningYear,
        metaData
      )
    ) {
      return planBy === "+/- Var"
        ? percentUnitFormatter(params, VARIANCE_DECIMALS)
        : Math.round(params.value).toFixed(0);
    }

    return formatCellValueByMetric(params, metaData);
  };
}
